/**
 * Frame renderer - applies all visual effects to each frame
 */

import { useEditorStore, ZoomRegion, TextLayer, ElementLayer, PerspectiveSettings } from '@/store/editor-store'
import { useRecordingStore } from '@/store/recording-store'
import { interpolateCursorPosition, drawCursorOnCanvas } from '@/lib/cursor-renderer'
import { computeZoomAtTime } from '@/lib/zoom-suggestions'

interface RenderContext {
    canvas: OffscreenCanvas
    ctx: OffscreenCanvasRenderingContext2D
    videoWidth: number
    videoHeight: number
    outputWidth: number
    outputHeight: number
    bgImage?: HTMLImageElement | ImageBitmap | null
}

/**
 * Create a render context for frame rendering
 */
export function createRenderContext(
    outputWidth: number,
    outputHeight: number,
    videoWidth: number,
    videoHeight: number,
    bgImage?: HTMLImageElement | ImageBitmap | null
): RenderContext {
    const canvas = new OffscreenCanvas(outputWidth, outputHeight)
    const ctx = canvas.getContext('2d', {
        alpha: false,
        desynchronized: true,
    })!

    return {
        canvas,
        ctx,
        videoWidth,
        videoHeight,
        outputWidth,
        outputHeight,
        bgImage
    }
}

/**
 * Calculate zoom transform for a given time using Recordly's timing model.
 * Delegates to computeZoomAtTime and converts x/y (% units) to pixel offsets.
 */
function calculateZoomTransform(
    time: number,
    zoomRegions: ZoomRegion[],
    areaWidth: number,
    areaHeight: number
): { scale: number; translateX: number; translateY: number } {
    const z = computeZoomAtTime(time, zoomRegions)
    return {
        scale: z.scale,
        translateX: (z.x / 100) * areaWidth,
        translateY: (z.y / 100) * areaHeight,
    }
}

/**
 * Calculates a 2D affine projection of a 3D rotation matrix (Rx * Ry * Rz)
 * with perspective foreshortening using actual 3D corner projection
 */
function get3DAffineProjection(settings: PerspectiveSettings, cardWidth: number, cardHeight: number) {
    const { rotateX, rotateY, rotateZ, scale, perspective: perspectiveDist } = settings
    const degToRad = Math.PI / 180

    const rx = rotateX * degToRad
    const ry = rotateY * degToRad
    const rz = rotateZ * degToRad

    const cx = Math.cos(rx), sx = Math.sin(rx)
    const cy = Math.cos(ry), sy = Math.sin(ry)
    const cz = Math.cos(rz), sz = Math.sin(rz)

    // Project a card-local 2D point through 3D rotation + perspective
    const project = (lx: number, ly: number): { x: number; y: number } => {
        // Rotate: R = Rz(rz) * Ry(ry) * Rx(rx) applied to (lx, ly, 0)
        const x3 = (cy * cz) * lx + (sx * sy * cz - cx * sz) * ly
        const y3 = (cy * sz) * lx + (sx * sy * sz + cx * cz) * ly
        const z3 = (-sy)     * lx + (sx * cy)               * ly

        const xS = x3 * scale
        const yS = y3 * scale
        const zS = z3 * scale

        const w = perspectiveDist / (perspectiveDist + zS)
        return { x: xS * w, y: yS * w }
    }

    // Map three card corners through the projection, then solve for the affine transform
    const hw = cardWidth / 2
    const hh = cardHeight / 2

    const tl = project(-hw, -hh)
    const tr = project( hw, -hh)
    const bl = project(-hw,  hh)

    // Solve: a*x + c*y + e = x', b*x + d*y + f = y'
    // Using tl, tr, bl gives exact solution for 6 unknowns
    const a = (tr.x - tl.x) / (2 * hw)
    const c = (bl.x - tl.x) / (2 * hh)
    const e = tl.x + a * hw + c * hh  // e = tl.x - a*(-hw) - c*(-hh) = tl.x + a*hw + c*hh

    const b = (tr.y - tl.y) / (2 * hw)
    const d = (bl.y - tl.y) / (2 * hh)
    const f = tl.y + b * hw + d * hh

    return { a, b, c, d, e, f }
}

/**
 * Parse angle from gradient (e.g., "135deg" -> angle in radians)
 */
function parseGradientAngle(angleStr: string): { x1: number; y1: number; x2: number; y2: number } {
    // Default: top to bottom (180deg)
    let angle = 180

    const degMatch = angleStr.match(/(\d+)deg/)
    if (degMatch) {
        angle = parseInt(degMatch[1], 10)
    } else if (angleStr.includes('to right')) {
        angle = 90
    } else if (angleStr.includes('to left')) {
        angle = 270
    } else if (angleStr.includes('to top')) {
        angle = 0
    } else if (angleStr.includes('to bottom')) {
        angle = 180
    } else if (angleStr.includes('to bottom right') || angleStr.includes('to right bottom')) {
        angle = 135
    } else if (angleStr.includes('to bottom left') || angleStr.includes('to left bottom')) {
        angle = 225
    } else if (angleStr.includes('to top right') || angleStr.includes('to right top')) {
        angle = 45
    } else if (angleStr.includes('to top left') || angleStr.includes('to left top')) {
        angle = 315
    }

    // Convert angle to coordinates
    // CSS angles: 0deg is up, 90deg is right, etc.
    // We need to map this to canvas coordinates
    const radians = (angle - 90) * (Math.PI / 180)

    // Calculate gradient line endpoints (normalized -1 to 1)
    const cos = Math.cos(radians)
    const sin = Math.sin(radians)

    // Return as 0-1 range for width/height multiplication
    return {
        x1: 0.5 - cos * 0.5,
        y1: 0.5 - sin * 0.5,
        x2: 0.5 + cos * 0.5,
        y2: 0.5 + sin * 0.5,
    }
}

/**
 * Parse color stops from gradient string
 */
function parseColorStops(gradientStr: string): Array<{ color: string; position: number }> {
    const stops: Array<{ color: string; position: number }> = []

    // Match color stops: color followed by optional percentage
    // e.g., "#667eea 0%", "rgba(255,0,0,0.5) 50%", "red 100%"
    const colorStopRegex = /(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|[a-zA-Z]+)\s*(\d+%)?/g

    let match
    const allMatches: Array<{ color: string; position: number | null }> = []

    // List of CSS gradient keywords to ignore as colors
    const keywords = ['to', 'top', 'bottom', 'left', 'right', 'deg', 'linear', 'gradient', 'at', 'center']

    while ((match = colorStopRegex.exec(gradientStr)) !== null) {
        const color = match[1]
        const positionStr = match[2]

        // Skip directional keywords and "deg"
        if (keywords.includes(color.toLowerCase())) continue

        let position: number | null = null
        if (positionStr) {
            position = parseInt(positionStr, 10) / 100
        }

        allMatches.push({ color, position })
    }

    // Fill in missing positions
    for (let i = 0; i < allMatches.length; i++) {
        if (allMatches[i].position === null) {
            if (i === 0) {
                allMatches[i].position = 0
            } else if (i === allMatches.length - 1) {
                allMatches[i].position = 1
            } else {
                // Interpolate between known stops
                let prevKnown = 0
                let nextKnown = 1
                for (let j = i - 1; j >= 0; j--) {
                    if (allMatches[j].position !== null) {
                        prevKnown = allMatches[j].position!
                        break
                    }
                }
                for (let j = i + 1; j < allMatches.length; j++) {
                    if (allMatches[j].position !== null) {
                        nextKnown = allMatches[j].position!
                        break
                    }
                }
                allMatches[i].position = (prevKnown + nextKnown) / 2
            }
        }
        stops.push({ color: allMatches[i].color, position: allMatches[i].position! })
    }

    return stops
}

/**
 * Draw background (solid, gradient, or image)
 */
function drawBackground(
    ctx: OffscreenCanvasRenderingContext2D,
    width: number,
    height: number,
    backgroundType: string,
    backgroundValue: string,
    bgImage?: HTMLImageElement | ImageBitmap | null
): void {
    if (backgroundType === 'solid') {
        ctx.fillStyle = backgroundValue
        ctx.fillRect(0, 0, width, height)
    } else if (backgroundType === 'gradient') {
        // Parse gradient string and create canvas gradient
        try {
            if (backgroundValue.includes('radial-gradient')) {
                // Fill base background color first if specified at end of string (e.g. #050515)
                const baseColorMatch = backgroundValue.match(/,?\s*(#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}|rgb\([^)]+\))\s*$/)
                ctx.fillStyle = baseColorMatch ? baseColorMatch[1] : '#050515'
                ctx.fillRect(0, 0, width, height)

                // Match each radial-gradient(...)
                const radialMatches = [...backgroundValue.matchAll(/radial-gradient\(([^)]+)\)/g)]
                for (const match of radialMatches) {
                    const params = match[1]
                    let xRatio = 0.5
                    let yRatio = 0.5
                    const posMatch = params.match(/at\s+([0-9.]+)%\s+([0-9.]+)%/)
                    if (posMatch) {
                        xRatio = parseFloat(posMatch[1]) / 100
                        yRatio = parseFloat(posMatch[2]) / 100
                    }

                    const colorMatch = params.match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}|rgba?\([^)]+\)/)
                    if (colorMatch) {
                        const color = colorMatch[0]
                        const cx = xRatio * width
                        const cy = yRatio * height
                        const radius = Math.max(width, height) * 0.75

                        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius)
                        grad.addColorStop(0, color)
                        grad.addColorStop(1, 'transparent')

                        ctx.fillStyle = grad
                        ctx.fillRect(0, 0, width, height)
                    }
                }
            } else {
                // Extract the content inside linear-gradient()
                const gradientContentMatch = backgroundValue.match(/linear-gradient\(([^)]+)\)/)
                if (gradientContentMatch) {
                    const content = gradientContentMatch[1]

                    // First part before the first color is the angle
                    const parts = content.split(',')
                    const angleStr = parts[0].trim()

                    // Parse angle and get gradient coordinates  
                    const coords = parseGradientAngle(angleStr)

                    // Create canvas gradient
                    const gradient = ctx.createLinearGradient(
                        coords.x1 * width,
                        coords.y1 * height,
                        coords.x2 * width,
                        coords.y2 * height
                    )

                    // Parse color stops from the rest of the gradient
                    const colorStops = parseColorStops(content)

                    if (colorStops.length >= 2) {
                        for (const stop of colorStops) {
                            gradient.addColorStop(stop.position, stop.color)
                        }

                        ctx.fillStyle = gradient
                        ctx.fillRect(0, 0, width, height)
                    } else {
                        // Fallback if we couldn't parse colors
                        ctx.fillStyle = '#1a1a2e'
                        ctx.fillRect(0, 0, width, height)
                    }
                } else {
                    // Fallback for non-linear gradients
                    ctx.fillStyle = '#1a1a2e'
                    ctx.fillRect(0, 0, width, height)
                }
            }
        } catch {
            // Fallback on any error
            ctx.fillStyle = '#1a1a2e'
            ctx.fillRect(0, 0, width, height)
        }
    } else if (backgroundType === 'image' || backgroundValue.startsWith('http') || backgroundValue.startsWith('blob:') || backgroundValue.startsWith('/')) {
        if (bgImage) {
            // Draw image with object-fit: cover logic
            const imgWidth = 'width' in bgImage ? bgImage.width : (bgImage as ImageBitmap).width
            const imgHeight = 'height' in bgImage ? bgImage.height : (bgImage as ImageBitmap).height

            const imgRatio = imgWidth / imgHeight
            const canvasRatio = width / height

            let drawWidth, drawHeight, x, y

            if (imgRatio > canvasRatio) {
                drawHeight = height
                drawWidth = height * imgRatio
                x = (width - drawWidth) / 2
                y = 0
            } else {
                drawWidth = width
                drawHeight = width / imgRatio
                x = 0
                y = (height - drawHeight) / 2
            }

            ctx.drawImage(bgImage, x, y, drawWidth, drawHeight)
        } else {
            // Fallback if image not loaded
            ctx.fillStyle = '#1a1a2e'
            ctx.fillRect(0, 0, width, height)
        }
    }
}

/**
 * Draw element layer (arrows, shapes, callouts)
 */
function drawElementLayer(
    ctx: OffscreenCanvasRenderingContext2D,
    layer: ElementLayer,
    canvasWidth: number,
    canvasHeight: number,
    time: number
): void {
    if (time < layer.startTime || time > layer.endTime) {
        return
    }

    const x = (layer.x / 100) * canvasWidth
    const y = (layer.y / 100) * canvasHeight

    ctx.save()
    ctx.translate(x, y)
    ctx.rotate((layer.rotation * Math.PI) / 180)
    ctx.globalAlpha = layer.opacity / 100

    const hw = layer.width / 2
    const hh = layer.height / 2

    ctx.lineWidth = layer.strokeWidth
    ctx.strokeStyle = layer.color
    ctx.fillStyle = layer.color

    if (layer.type === 'rectangle') {
        ctx.strokeRect(-hw, -hh, layer.width, layer.height)
    } else if (layer.type === 'ellipse') {
        ctx.beginPath()
        ctx.ellipse(0, 0, hw, hh, 0, 0, Math.PI * 2)
        ctx.stroke()
    } else if (layer.type === 'arrow') {
        ctx.beginPath()
        ctx.moveTo(-hw, 0)
        ctx.lineTo(hw, 0)
        ctx.stroke()

        // Draw arrowhead
        ctx.beginPath()
        ctx.moveTo(hw, 0)
        ctx.lineTo(hw - 10, -5)
        ctx.lineTo(hw - 10, 5)
        ctx.fill()
    } else if (layer.type === 'callout') {
        // Draw callout box
        ctx.beginPath()
        ctx.roundRect(-hw, -hh, layer.width, layer.height, 8)
        ctx.fill()
        ctx.strokeStyle = 'rgba(255,255,255,0.2)'
        ctx.stroke()

        // Draw tail
        ctx.beginPath()
        ctx.moveTo(-hw + 20, hh)
        ctx.lineTo(-hw + 30, hh + 10)
        ctx.lineTo(-hw + 40, hh)
        ctx.fill()

        // Draw text
        if (layer.content && layer.fontSize && layer.fontFamily && layer.colorText) {
            ctx.fillStyle = layer.colorText
            ctx.font = `${layer.fontSize}px ${layer.fontFamily}`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            
            // Simple word wrap
            const words = layer.content.split(' ')
            let line = ''
            let lines = []
            for (let n = 0; n < words.length; n++) {
                const testLine = line + words[n] + ' '
                const metrics = ctx.measureText(testLine)
                if (metrics.width > layer.width - 16 && n > 0) {
                    lines.push(line)
                    line = words[n] + ' '
                } else {
                    line = testLine
                }
            }
            lines.push(line)
            
            const lineHeight = layer.fontSize * 1.2
            const totalHeight = lines.length * lineHeight
            const startY = -totalHeight / 2 + lineHeight / 2
            
            lines.forEach((l, i) => {
                ctx.fillText(l.trim(), 0, startY + (i * lineHeight))
            })
        }
    }

    ctx.restore()
}

/**
 * Draw text layer with scaling
 */
function drawTextLayer(
    ctx: OffscreenCanvasRenderingContext2D,
    layer: TextLayer,
    canvasWidth: number,
    canvasHeight: number,
    time: number,
    scale: number
): void {
    // Check if layer is visible at this time
    if (time < layer.startTime || time > layer.endTime) {
        return
    }

    const x = (layer.x / 100) * canvasWidth
    const y = (layer.y / 100) * canvasHeight

    // Typewriter: slice content based on elapsed time (same formula as CanvasPreview)
    let displayContent = layer.content
    if (layer.animationIn === 'typewriter') {
        const elapsed = time - layer.startTime
        const twDur = Math.min(layer.content.length * 0.05, 2.0)
        const chars = elapsed >= twDur
            ? layer.content.length
            : Math.floor((elapsed / twDur) * layer.content.length)
        displayContent = layer.content.slice(0, Math.max(0, chars))
        if (!displayContent) return // nothing to draw yet
    }

    ctx.save()

    // Handle Entrance Animation
    const activeTime = time - layer.startTime
    const animDuration = 0.5

    if (activeTime < animDuration && activeTime >= 0 && layer.animationIn !== 'typewriter') {
        const t = activeTime / animDuration

        // Bounce easing helper
        const bounceOut = (v: number): number => {
            if (v < 4 / 11) return (121 * v * v) / 16
            if (v < 8 / 11) return (363 / 40 * v * v) - (99 / 10 * v) + 17 / 5
            if (v < 9 / 10) return (4356 / 361 * v * v) - (35442 / 1805 * v) + 16061 / 1805
            return (54 / 5 * v * v) - (513 / 25 * v) + 268 / 25
        }

        if (layer.animationIn === 'pop') {
            const popS = Math.min(1, t * 1.5)
            ctx.translate(x, y)
            ctx.scale(popS, popS)
            ctx.translate(-x, -y)
        } else if (layer.animationIn === 'fade') {
            ctx.globalAlpha *= t
        } else if (layer.animationIn === 'slide-up') {
            ctx.translate(0, 30 * (1 - t) * scale)
            ctx.globalAlpha *= t
        } else if (layer.animationIn === 'slide-down') {
            ctx.translate(0, -30 * (1 - t) * scale)
            ctx.globalAlpha *= t
        } else if (layer.animationIn === 'slide-left') {
            ctx.translate(30 * (1 - t) * scale, 0)
            ctx.globalAlpha *= t
        } else if (layer.animationIn === 'slide-right') {
            ctx.translate(-30 * (1 - t) * scale, 0)
            ctx.globalAlpha *= t
        } else if (layer.animationIn === 'zoom-in') {
            const zs = 1.35 - 0.35 * t
            ctx.translate(x, y)
            ctx.scale(zs, zs)
            ctx.translate(-x, -y)
            ctx.globalAlpha *= t
        } else if (layer.animationIn === 'blur-in') {
            // Canvas filter support for blur
            const blurPx = 8 * (1 - t)
            ctx.filter = `blur(${blurPx.toFixed(1)}px)`
            ctx.globalAlpha *= t
        } else if (layer.animationIn === 'bounce') {
            const yOffset = -20 * scale * (1 - bounceOut(t))
            ctx.translate(0, yOffset)
            if (t < 0.2) ctx.globalAlpha *= t / 0.2
        } else if (layer.animationIn === 'flip-x') {
            // Horizontal flip: scaleX 0 → 1
            const flipS = t
            ctx.translate(x, y)
            ctx.scale(flipS, 1)
            ctx.translate(-x, -y)
        }
    }

    // Handle Exit Transition (matches exit={{ opacity: 0, scale: 0.95 }} in preview)
    const timeUntilEnd = layer.endTime - time
    const exitDuration = 0.25 // 250ms duration
    if (timeUntilEnd < exitDuration && timeUntilEnd >= 0) {
        const t = timeUntilEnd / exitDuration // 1.0 (start exit) to 0.0 (fully exited)
        ctx.globalAlpha *= t
        
        const exitScale = 0.95 + 0.05 * t
        ctx.translate(x, y)
        ctx.scale(exitScale, exitScale)
        ctx.translate(-x, -y)
    }

    // Set font with scaled size
    const scaledFontSize = layer.fontSize * scale
    ctx.font = `${layer.fontWeight} ${scaledFontSize}px ${layer.fontFamily}`
    ctx.textAlign = layer.textAlign
    ctx.textBaseline = 'top'

    const metrics = ctx.measureText(displayContent)

    // Draw background if enabled
    if (layer.backgroundColor && layer.backgroundOpacity && layer.backgroundOpacity > 0) {
        const padding = (layer.backgroundPadding || 8) * scale
        const bgWidth = metrics.width + padding * 2

        // Use scaled line height or font size
        const bgHeight = scaledFontSize * (layer.lineHeight || 1.2) + padding * 2
        const radius = (layer.backgroundBorderRadius || 4) * scale

        ctx.globalAlpha = layer.backgroundOpacity
        ctx.fillStyle = layer.backgroundColor
        ctx.beginPath()
        ctx.roundRect(
            x - (layer.textAlign === 'center' ? bgWidth / 2 : layer.textAlign === 'right' ? bgWidth : 0) - padding,
            y - padding,
            bgWidth,
            bgHeight,
            radius
        )
        ctx.fill()
        ctx.globalAlpha = 1
    }

    // Apply shadow if no background and not gradient
    if (!layer.backgroundColor && !layer.textGradient) {
        ctx.shadowColor = 'rgba(0,0,0,0.3)'
        ctx.shadowBlur = 4 * scale
        ctx.shadowOffsetY = 2 * scale
    }

    // Draw stroke if enabled
    if (layer.strokeEnabled && layer.strokeColor && layer.strokeWidth) {
        ctx.strokeStyle = layer.strokeColor
        ctx.lineWidth = layer.strokeWidth * scale
        ctx.strokeText(displayContent, x, y)
    }

    // Draw text
    ctx.globalAlpha = layer.opacity

    if (layer.textGradient && layer.textGradient.includes('linear-gradient')) {
        // Approximate CSS text gradient in canvas
        try {
            const gradient = ctx.createLinearGradient(x - metrics.width / 2, y, x + metrics.width / 2, y + scaledFontSize)
            // Fast parse: extract colors
            const colors = layer.textGradient.match(/#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|[a-zA-Z]+/g)
            if (colors && colors.length >= 2) {
                colors.forEach((c, i) => {
                    gradient.addColorStop(i / (colors.length - 1), c)
                })
                ctx.fillStyle = gradient
            } else {
                ctx.fillStyle = layer.color
            }
        } catch {
            ctx.fillStyle = layer.color
        }
    } else {
        ctx.fillStyle = layer.color
    }

    ctx.fillText(displayContent, x, y)

    ctx.restore()
}


/**
 * Render a single frame with all effects applied
 */
export function renderFrame(
    context: RenderContext,
    videoFrame: VideoFrame | null,
    time: number,
    editorState: ReturnType<typeof useEditorStore.getState>,
    cameraFrame: VideoFrame | null = null
): ImageBitmap {
    const { ctx, canvas, outputWidth, outputHeight, videoWidth, videoHeight } = context

    const {
        backgroundType,
        backgroundValue,
        backgroundBlur,
        backgroundDim,
        padding,
        borderRadius,
        zoomRegions,
        textLayers,
        elementLayers,
        videoCrop,
        borderWidth,
        borderColor,
        borderOpacity,
        shadow,
        perspective,
        perspectiveSettings,
        clipContent,
        animationIn,
        animationOut,
        cameraLayer,
        duration,
        trackVisibility
    } = editorState

    const visibility = trackVisibility || { video: true, zoom: true, text: true, camera: true, music: true, sfx: true }

    // Reference width for scaling layout values (padding, radius, etc.)
    // Matches the "standard" preview size where those values were designed
    const REFERENCE_WIDTH = 1000
    const s = outputWidth / REFERENCE_WIDTH

    const scaledPadding = padding * s
    const scaledBorderRadius = borderRadius * s
    const scaledBorderWidth = borderWidth * s

    // Clear canvas
    ctx.clearRect(0, 0, outputWidth, outputHeight)

    // 1. Draw background with blur if enabled
    ctx.save()
    if (backgroundBlur > 0) {
        // Apply blur filter
        ctx.filter = `blur(${backgroundBlur * s}px)`
        // Scale up slightly to prevent edge peaking (same as preview)
        ctx.translate(outputWidth / 2, outputHeight / 2)
        ctx.scale(1.1, 1.1)
        ctx.translate(-outputWidth / 2, -outputHeight / 2)
    }

    drawBackground(ctx, outputWidth, outputHeight, backgroundType, backgroundValue, context.bgImage)
    ctx.restore()

    // 1.2 Draw Dim Layer if enabled
    if (backgroundDim > 0) {
        ctx.fillStyle = `rgba(0,0,0,${backgroundDim})`
        ctx.fillRect(0, 0, outputWidth, outputHeight)
    }

    // 1.5 Apply Global Clipping if enabled (prevents elements from drawing outside canvas)
    if (clipContent) {
        ctx.save()
        ctx.beginPath()
        ctx.rect(0, 0, outputWidth, outputHeight)
        ctx.clip()
    }

    // 2. Calculate card area (accounting for padding)
    const videoAreaX = scaledPadding
    const videoAreaY = scaledPadding
    const videoAreaWidth = outputWidth - scaledPadding * 2
    const videoAreaHeight = outputHeight - scaledPadding * 2

    // Use the VIDEO's natural ratio for the card — matches preview exactly.
    // Card ratio = video ratio → video fills card edge-to-edge, zero black bars, zero cropping.
    let videoCardRatio: number
    if (videoCrop) {
        const srcW = (videoCrop.width / 100) * videoWidth
        const srcH = (videoCrop.height / 100) * videoHeight
        videoCardRatio = srcW / srcH
    } else {
        videoCardRatio = videoWidth / videoHeight
    }

    // Fit the card within the padded area using the video's own ratio.
    let cardWidth, cardHeight
    const areaRatio = videoAreaWidth / videoAreaHeight

    if (videoCardRatio > areaRatio) {
        // Video is wider than available area → width is the constraint
        cardWidth = videoAreaWidth
        cardHeight = videoAreaWidth / videoCardRatio
    } else {
        // Video is taller → height is the constraint
        cardHeight = videoAreaHeight
        cardWidth = videoAreaHeight * videoCardRatio
    }

    const cardX = videoAreaX + (videoAreaWidth - cardWidth) / 2
    const cardY = videoAreaY + (videoAreaHeight - cardHeight) / 2

    // 3. Calculate zoom transform
    const zoom = calculateZoomTransform(time, zoomRegions, cardWidth, cardHeight)

    // 4. Handle Entrance & Exit Animations
    const animDur = 0.5
    if (animationIn && animationIn !== 'none' && time < animDur) {
        const t = Math.max(0, Math.min(1, time / animDur))
        ctx.translate(outputWidth / 2, outputHeight / 2)
        if (animationIn === 'pop') {
            const popS = t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1
            ctx.scale(popS, popS)
        } else if (animationIn === 'fade') {
            ctx.globalAlpha *= t
        } else if (animationIn === 'slide-up') {
            ctx.translate(0, (1 - t) * 60)
            ctx.globalAlpha *= t
        } else if (animationIn === 'slide-down') {
            ctx.translate(0, -(1 - t) * 60)
            ctx.globalAlpha *= t
        } else if (animationIn === 'slide-left') {
            ctx.translate((1 - t) * 80, 0)
            ctx.globalAlpha *= t
        } else if (animationIn === 'slide-right') {
            ctx.translate(-(1 - t) * 80, 0)
            ctx.globalAlpha *= t
        } else if (animationIn === 'zoom-in') {
            const sc = 0.2 + 0.8 * t
            ctx.scale(sc, sc)
            ctx.globalAlpha *= t
        } else if (animationIn === 'blur-in') {
            ctx.globalAlpha *= t
        } else if (animationIn === 'bounce-in') {
            const sc = t < 0.5 ? 0.3 + 1.56 * t : t < 0.7 ? 1.08 - 0.8 * (t - 0.5) : 0.92 + 0.26 * (t - 0.7)
            ctx.scale(sc, sc)
            ctx.globalAlpha *= Math.min(1, t * 2)
        } else if (animationIn === 'rotate-in') {
            const rot = (1 - t) * -0.2
            const sc = 0.8 + 0.2 * t
            ctx.rotate(rot)
            ctx.scale(sc, sc)
            ctx.globalAlpha *= t
        } else if (animationIn === 'back-in') {
            const ty = (1 - t) * 40
            const sc = 0.7 + 0.3 * t
            ctx.translate(0, ty)
            ctx.scale(sc, sc)
            ctx.globalAlpha *= t
        }
        ctx.translate(-outputWidth / 2, -outputHeight / 2)
    } else if (animationOut && animationOut !== 'none' && duration > 1 && time >= (duration - animDur)) {
        const t = Math.max(0, Math.min(1, (time - (duration - animDur)) / animDur))
        const remainingT = 1 - t
        ctx.translate(outputWidth / 2, outputHeight / 2)
        if (animationOut === 'fade-out') {
            ctx.globalAlpha *= remainingT
        } else if (animationOut === 'slide-out-up') {
            ctx.translate(0, -t * 60)
            ctx.globalAlpha *= remainingT
        } else if (animationOut === 'slide-out-down') {
            ctx.translate(0, t * 60)
            ctx.globalAlpha *= remainingT
        } else if (animationOut === 'slide-out-left') {
            ctx.translate(-t * 80, 0)
            ctx.globalAlpha *= remainingT
        } else if (animationOut === 'slide-out-right') {
            ctx.translate(t * 80, 0)
            ctx.globalAlpha *= remainingT
        } else if (animationOut === 'zoom-out') {
            const sc = 1 - 0.7 * t
            ctx.scale(sc, sc)
            ctx.globalAlpha *= remainingT
        } else if (animationOut === 'blur-out') {
            ctx.globalAlpha *= remainingT
        } else if (animationOut === 'bounce-out') {
            const sc = 1 - 0.7 * t
            ctx.scale(sc, sc)
            ctx.globalAlpha *= remainingT
        } else if (animationOut === 'rotate-out') {
            ctx.rotate(t * 0.2)
            ctx.scale(1 - 0.2 * t, 1 - 0.2 * t)
            ctx.globalAlpha *= remainingT
        }
        ctx.translate(-outputWidth / 2, -outputHeight / 2)
    }

    // 5. Handle Perspective Transformation
    ctx.save()
    if (perspective && perspective !== 'none') {
        const matrix = get3DAffineProjection(perspectiveSettings, cardWidth, cardHeight)
        const cardCenterX = cardX + cardWidth / 2
        const cardCenterY = cardY + cardHeight / 2
        ctx.translate(cardCenterX, cardCenterY)
        ctx.transform(matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f)
        ctx.translate(-cardCenterX, -cardCenterY)
    }

    // 6. Draw Shadow
    if (shadow && shadow !== 'none' && (scaledPadding > 5)) {
        ctx.save()
        let blur = 40, offset = 20, opacity = 0.5
        if (shadow.includes('25px 50px')) { blur = 50; offset = 25; opacity = 0.5 }
        else if (shadow.includes('20px 25px')) { blur = 25; offset = 20; opacity = 0.1 }
        else if (shadow.includes('10px 15px')) { blur = 15; offset = 10; opacity = 0.1 }
        else if (shadow.includes('4px 6px')) { blur = 6; offset = 4; opacity = 0.1 }
        ctx.shadowColor = `rgba(0,0,0,${opacity})`
        ctx.shadowBlur = blur * s
        ctx.shadowOffsetY = offset * s
        ctx.fillStyle = '#000'
        ctx.beginPath()
        ctx.roundRect(cardX, cardY, cardWidth, cardHeight, scaledBorderRadius)
        ctx.fill()
        ctx.restore()
    }

    // 7. Draw main video content
    if (videoFrame && visibility.video) {
        ctx.save()
        if (scaledBorderRadius > 0) {
            ctx.beginPath()
            ctx.roundRect(cardX, cardY, cardWidth, cardHeight, scaledBorderRadius)
            ctx.clip()
        }

        // Card matches the video's ratio exactly — draw the video to fill the card
        // edge-to-edge. No black bars. No cropping. Mirrors the preview exactly.
        const centerX_card = cardX + cardWidth / 2
        const centerY_card = cardY + cardHeight / 2
        ctx.translate(centerX_card, centerY_card)
        ctx.scale(zoom.scale, zoom.scale)
        ctx.translate(zoom.translateX, zoom.translateY)

        let sourceX = 0
        let sourceY = 0
        let sourceWidth = videoWidth
        let sourceHeight = videoHeight

        if (videoCrop) {
            sourceX = (videoCrop.x / 100) * videoWidth
            sourceY = (videoCrop.y / 100) * videoHeight
            sourceWidth = (videoCrop.width / 100) * videoWidth
            sourceHeight = (videoCrop.height / 100) * videoHeight
        }

        // Draw to fill the card exactly — card ratio == source ratio, so no distortion.
        ctx.drawImage(
            videoFrame,
            sourceX, sourceY, sourceWidth, sourceHeight,
            -cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight
        )

        ctx.restore()
    }

    // 8. Draw border (outside the card — stroke expands outward, doesn't eat into content)
    if (scaledBorderWidth > 0) {
        const r = parseInt(borderColor.slice(1, 3), 16)
        const g = parseInt(borderColor.slice(3, 5), 16)
        const b = parseInt(borderColor.slice(5, 7), 16)
        ctx.strokeStyle = `rgba(${r},${g},${b},${borderOpacity})`
        ctx.lineWidth = scaledBorderWidth
        ctx.beginPath()
        ctx.roundRect(
            cardX - scaledBorderWidth / 2,
            cardY - scaledBorderWidth / 2,
            cardWidth + scaledBorderWidth,
            cardHeight + scaledBorderWidth,
            scaledBorderRadius + scaledBorderWidth / 2
        )
        ctx.stroke()
    }

    ctx.restore() // Remove 3D transform / Entrance animations for text and camera

    // 9. Draw camera layer
    const camRange = cameraLayer.timeRange
    const cameraActiveAtTime = !camRange || (time >= camRange.start && time <= camRange.end)
    if (cameraLayer.visible && visibility.camera && cameraFrame && cameraActiveAtTime) {
        ctx.save()
        const camX = (cameraLayer.x / 100) * outputWidth
        const camY = (cameraLayer.y / 100) * outputHeight
        const camSize = (cameraLayer.size / 100) * outputWidth
        const camRatio = cameraLayer.mask === 'rectangle' ? 16 / 9 : 1 / 1
        const camWidth = camSize
        const camHeight = camSize / camRatio

        // Apply masking (circle/rectangle)
        ctx.save()
        ctx.translate(camX, camY)
        ctx.translate(-camWidth / 2, -camHeight / 2) // Center the camera box at (camX, camY)

        if (cameraLayer.mask === 'circle') {
            ctx.beginPath()
            ctx.arc(camWidth / 2, camHeight / 2, camWidth / 2, 0, Math.PI * 2)
            ctx.clip()
        } else {
            const radius = camWidth * ((cameraLayer.borderRadius ?? 35) / 100)
            ctx.beginPath()
            ctx.roundRect(0, 0, camWidth, camHeight, radius)
            ctx.clip()
        }

        // Draw camera frame (object-fit cover)
        const frameRatio = cameraFrame.displayWidth / cameraFrame.displayHeight
        let cdWidth, cdHeight, cx, cy

        if (frameRatio > camRatio) {
            cdHeight = camHeight
            cdWidth = camHeight * frameRatio
            cx = (camWidth - cdWidth) / 2
            cy = 0
        } else {
            cdWidth = camWidth
            cdHeight = camWidth / frameRatio
            cx = 0
            cy = (camHeight - cdHeight) / 2
        }

        ctx.drawImage(cameraFrame, cx, cy, cdWidth, cdHeight)
        ctx.restore()

        // Draw camera border
        if (cameraLayer.borderWidth > 0) {
            ctx.strokeStyle = cameraLayer.borderColor
            ctx.lineWidth = cameraLayer.borderWidth * s
            ctx.beginPath()
            if (cameraLayer.mask === 'circle') {
                ctx.arc(camX, camY, camWidth / 2, 0, Math.PI * 2)
            } else {
                const radius = camWidth * ((cameraLayer.borderRadius ?? 35) / 100)
                ctx.roundRect(camX - camWidth / 2, camY - camHeight / 2, camWidth, camHeight, radius)
            }
            ctx.stroke()
        }
        ctx.restore()
    }

    // 9.5 Draw cursor overlay (on top of video, inside card area)
    if (editorState.cursorSettings?.visible) {
        const recordingState = useRecordingStore.getState()
        const mouseData = recordingState.mouseData
        // Skip cursor shape for extension (baked in), but not for desktop (uiohook overlay)
        const skipCursorShape = recordingState.isExtension && !recordingState.isDesktop
        if (mouseData && mouseData.length > 0) {
            const cursorPos = interpolateCursorPosition(
                mouseData,
                time * 1000,
                recordingState.screenWidth,
                recordingState.screenHeight,
                editorState.cursorSettings.smoothing
            )
            if (cursorPos) {
                // Cursor coordinates are in video space (0-1 normalized).
                // Adjust for zoom: CSS scale(S) translate(tx%,ty%) shifts a point (cx,cy)
                // to appear at 0.5 + S*(cx + tx/100 - 0.5) within the card.
                const rawZoom = computeZoomAtTime(time, zoomRegions)
                const zS = rawZoom.scale
                const zTx = rawZoom.x / 100
                const zTy = rawZoom.y / 100
                const adjX = (cx: number) => 0.5 + zS * (cx + zTx - 0.5)
                const adjY = (cy: number) => 0.5 + zS * (cy + zTy - 0.5)
                // Map zoom-adjusted video-space position to output canvas space
                drawCursorOnCanvas(
                    ctx,
                    {
                        ...cursorPos,
                        x: cardX / outputWidth + adjX(cursorPos.x) * (cardWidth / outputWidth),
                        y: cardY / outputHeight + adjY(cursorPos.y) * (cardHeight / outputHeight),
                        lastClickX: cardX / outputWidth + adjX(cursorPos.lastClickX) * (cardWidth / outputWidth),
                        lastClickY: cardY / outputHeight + adjY(cursorPos.lastClickY) * (cardHeight / outputHeight),
                    },
                    editorState.cursorSettings,
                    outputWidth,
                    outputHeight,
                    skipCursorShape  // cursor shape is baked into the video for extension recordings
                )
            }
        }
    }

    // 10. Draw text layers (Drawn on top of everything)
    if (visibility.text) {
        for (const layer of textLayers) {
            drawTextLayer(ctx, layer, outputWidth, outputHeight, time, s)
        }
    }

    // 11. Draw element layers (arrows, shapes, callouts)
    if (visibility.elements !== false) {
        for (const layer of elementLayers) {
            drawElementLayer(ctx, layer, outputWidth, outputHeight, time)
        }
    }

    if (clipContent) {
        ctx.restore() // End global clip
    }

    // Create ImageBitmap from canvas
    return canvas.transferToImageBitmap()
}

/**
 * Dispose of render context resources
 */
export function disposeRenderContext(context: RenderContext): void {
    // OffscreenCanvas doesn't need explicit disposal
    // but we can clear references
    context.ctx.clearRect(0, 0, context.outputWidth, context.outputHeight)
}
